import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, Button, Box, Typography, CircularProgress } from '@mui/material';
import { userManager } from '../../auth/oidcConfig';

interface LoginPageProps {
  onLoginSuccess: () => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const [error, setError] = useState<string | null>(null);
  // Recomputed on every render: clearing the error also drops us off /callback.
  const isCallback = window.location.pathname === '/callback' && !error;

  useEffect(() => {
    if (!isCallback) return;

    userManager
      .signinRedirectCallback()
      .then(() => onLoginSuccess())
      .catch((err: Error) => {
        // Fail loud: a silent catch leaves the operator on an endless spinner.
        setError(err.message || 'Could not complete sign-in.');
      });
  }, [isCallback, onLoginSuccess]);

  const onRetry = () => {
    // Drop the spent authorization code from the URL before offering a retry —
    // re-running the callback with it would fail again.
    window.history.replaceState({}, '', '/');
    setError(null);
  };

  if (isCallback) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress aria-label="Completing sign-in" />
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      gap={3}
    >
      <Typography variant="h4" component="h1">
        Carrier Pigeon Fleet
      </Typography>
      <Typography variant="body1" color="text.secondary">
        Sign in to manage your drone fleet
      </Typography>
      {error && (
        <Alert severity="error" role="alert" sx={{ maxWidth: 480 }}>
          <AlertTitle>Sign-in failed</AlertTitle>
          {error}
          <Box mt={1}>
            <Button size="small" variant="outlined" color="inherit" onClick={onRetry}>
              Try again
            </Button>
          </Box>
        </Alert>
      )}
      <Button
        variant="contained"
        size="large"
        onClick={() => userManager.signinRedirect()}
      >
        Sign in
      </Button>
    </Box>
  );
};
